import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ticketRef } from './ticketRef';
import clsx from 'clsx';
import { PiXLight, PiCloudArrowDownLight, PiArrowsOutSimpleLight, PiPaperPlaneTiltLight, PiImageLight, PiFilePdfLight, PiFileDocLight, PiFileXlsLight, PiFileTextLight, PiFileZipLight, PiFileVideoLight, PiFileAudioLight, PiPaperclipLight, PiPaperclipHorizontalLight } from 'react-icons/pi';
import apis from '../../../utils/apis';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import GlassCard from '../../shared/brand/GlassCard';
import RichText from '../../shared/RichText';
import { Field, InfoRow, Well } from '../../shared/brand/Field';
import { useAppDispatch } from '../../../store';
import { createTicketComment, fetchTicket, fetchTicketComments } from '../../../store/actions/HelpAction';

/**
 * Parse a backend timestamp into a Date rendered in the viewer's LOCAL time.
 * Backend timestamps are UTC; naive ones (no tz suffix) are marked UTC so the
 * local time isn't off by the browser's offset.
 */
const toDate = (v: string): Date =>
  new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(String(v)) ? v : `${v}Z`);

interface TicketDetailsContentProps {
  ticket: any;
}

const TicketDetailsContent: React.FC<TicketDetailsContentProps> = ({ ticket }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageCache, setImageCache] = useState<{ [key: string]: string }>({});
  const [comments, setComments] = useState<any[]>([]);
  const [commentText, setCommentText] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'comments' | 'attachments'>('details');

  const formattedSupportId = ticketRef(ticket.id || ticket.ticket_id).slice(1);

  // Fetch comments on component mount
  useEffect(() => {
    const loadComments = async () => {
      setIsLoadingComments(true);
      const result = await dispatch(fetchTicketComments(ticket.id) as any);
      if (result?.comments) {
        setComments(result.comments);
      }
      setIsLoadingComments(false);
    };
    loadComments();
  }, [ticket.id, dispatch]);

  const getStatusColor = (status: string): 'gray' | 'yellow' | 'blue' | 'green' | 'orange' | 'red' => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'blue';
      case 'in_progress':
        return 'blue';
      case 'closed':
        return 'green';
      case 'pending_user':
        return 'orange';
      default:
        return 'blue';
    }
  };

  const getPriorityColor = (priority: string): 'gray' | 'green' | 'yellow' | 'orange' | 'red' => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const isImageFile = (filename: string, contentType?: string): boolean => {
    const ext = getFileExtension(filename).toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'];
    if (imageExtensions.includes(ext)) return true;
    return contentType?.startsWith('image/') || false;
  };

  const getFileIcon = (filename: string, contentType?: string): JSX.Element => {
    const ext = getFileExtension(filename).toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'].includes(ext)) return <PiImageLight size={20} />;
    if (ext === 'pdf') return <PiFilePdfLight size={20} />;
    if (['doc', 'docx', 'docm', 'dot', 'dotx'].includes(ext)) return <PiFileDocLight size={20} />;
    if (['xls', 'xlsx', 'xlsm', 'csv'].includes(ext)) return <PiFileXlsLight size={20} />;
    if (['txt', 'md', 'json', 'xml', 'yaml', 'yml'].includes(ext)) return <PiFileTextLight size={20} />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return <PiFileZipLight size={20} />;
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return <PiFileVideoLight size={20} />;
    if (['mp3', 'wav', 'aac', 'flac', 'ogg'].includes(ext)) return <PiFileAudioLight size={20} />;
    return <PiPaperclipLight size={20} />;
  };

  const getImageDataUrl = async (attachment: any): Promise<string> => {
    if (imageCache[attachment.attachment_id]) {
      return imageCache[attachment.attachment_id];
    }

    try {
      const downloadUrl = `/support/tickets/${ticket.id}/attachments/${attachment.attachment_id}/download`;
      const response = await apis({
        method: 'GET',
        url: downloadUrl,
        responseType: 'blob',
      });

      const dataUrl = URL.createObjectURL(response.data);
      setImageCache(prev => ({ ...prev, [attachment.attachment_id]: dataUrl }));
      return dataUrl;
    } catch (error) {
      console.error('Failed to load image:', error);
      return '';
    }
  };

  useEffect(() => {
    if (ticket.attachments && ticket.attachments.length > 0) {
      ticket.attachments.forEach((attachment: any) => {
        if (isImageFile(attachment.filename, attachment.content_type) && !imageCache[attachment.attachment_id]) {
          getImageDataUrl(attachment);
        }
      });
    }
  }, [ticket.id, ticket.attachments]);

  const handleImageClick = async (attachment: any) => {
    const dataUrl = await getImageDataUrl(attachment);
    setSelectedImage(dataUrl);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const handleDownload = async (attachment: any) => {
    try {
      const downloadUrl = `/support/tickets/${ticket.id}/attachments/${attachment.attachment_id}/download`;
      const response = await apis({
        method: 'GET',
        url: downloadUrl,
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename || 'attachment');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download attachment:', error);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('file', files[0]);

      const response = await apis({
        method: 'POST',
        url: `/support/tickets/${ticket.id}/attachments/upload`,
        data: formData,
      });

      if (response.data && response.data.success) {
        // Pull the ticket back down so the new attachment appears. A full page
        // reload would also throw away the comments already fetched here.
        await dispatch(fetchTicket(ticket.id) as any);
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handlePostComment = async () => {
    if (!commentText.trim()) return;

    setIsPostingComment(true);
    const result = await dispatch(createTicketComment(ticket.id, commentText) as any);
    
    if (result?.success) {
      setCommentText('');
      // Refresh comments
      const updatedComments = await dispatch(fetchTicketComments(ticket.id) as any);
      if (updatedComments?.comments) {
        setComments(updatedComments.comments);
      }
    }
    setIsPostingComment(false);
  };

  return (
    <div className="w-full h-[calc(100vh-110px)] flex flex-col gap-3 sm:gap-4 p-3 sm:p-4 max-w-4xl mx-auto">
      {/* Header — ID, title, status + priority. Stacks on mobile. */}
      <GlassCard padding="lg" className="flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <p className="mb-1 text-xs text-fg-muted">#{formattedSupportId}</p>
            <h2 className="break-words text-lg font-medium text-fg-strong">{ticket.title}</h2>
            <p className="text-xs text-fg-muted mt-1">{t('Created')} {ticket.created_at ? format(toDate(ticket.created_at), 'd MMM yyyy, HH:mm') : ''}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge color={getStatusColor(ticket.status)} className="capitalize text-xs">{ticket.status || 'open'}</Badge>
            <Badge color={getPriorityColor(ticket.priority)} className="capitalize text-xs">{ticket.priority || 'medium'}</Badge>
          </div>
        </div>
      </GlassCard>

      {/* Tabs — champagne underline marks the active one. Scrolls on narrow screens. */}
      <div data-tour-id="ticket-tabs" className="flex-shrink-0 flex gap-1 border-b border-hairline-soft overflow-x-auto">
        {([['details', t('Details')], ['comments', `${t('Comments')} (${comments.length})`], ['attachments', `${t('Attachments')} (${ticket.attachments?.length || 0})`]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key as 'details' | 'comments' | 'attachments')}
            className={clsx(
              'px-4 py-2 text-sm font-medium -mb-px border-b-2 whitespace-nowrap transition-colors',
              activeTab === key ? 'border-champagne text-fg-strong' : 'border-transparent text-fg-muted hover:text-fg-body'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content — fills the remaining height, consistent across tabs. */}
      <div className="flex-1 min-h-0">
      {/* DETAILS */}
      {activeTab === 'details' && (
        /*
          Feedback round 3: status and priority already sit in the header, so
          the details tab no longer repeats them, and the card is as tall as
          its content instead of a page-high box with three lines in it.
          The description - the thing someone opened the ticket to read -
          comes first, in a Well.
        */
        <GlassCard padding="lg" className="max-h-full overflow-y-auto">
          <div className="space-y-5">
          <div data-tour-id="ticket-description">
            <p className="mb-2 text-sm font-medium text-fg-strong">{t('Description')}</p>
            <Well className="whitespace-pre-wrap text-sm leading-relaxed text-fg-body">
              {ticket.description}
            </Well>
          </div>

          <div data-tour-id="ticket-status-priority" className="space-y-2">
            <InfoRow
              label={t('Created')}
              value={ticket.created_at ? format(toDate(ticket.created_at), 'd MMM yyyy, HH:mm') : undefined}
            />
            <InfoRow
              label={t('Updated')}
              value={ticket.updated_at ? format(toDate(ticket.updated_at), 'd MMM yyyy, HH:mm') : undefined}
            />
          </div>

          {/* Resolution */}
          {ticket.resolution && (
            <Field label={t('Resolution')} value={ticket.resolution} />
          )}
          </div>
        </GlassCard>
      )}

      {/* ATTACHMENTS */}
      {activeTab === 'attachments' && (
        <GlassCard
          data-tour-id="ticket-attachments"
          title={
            <>
              {t('Attachments')}
              {ticket.attachments && ticket.attachments.length > 0 && ` (${ticket.attachments.length})`}
            </>
          }
          actions={
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
              />
              <Button
                variant="outline"
                size="small"
                icon={<PiPaperclipHorizontalLight size={14} />}
                isLoading={isUploadingFile}
                onClick={() => fileInputRef.current?.click()}
              >
                {t('Upload')}
              </Button>
            </>
          }
          className="h-full overflow-hidden flex flex-col"
          bodyClassName="flex-1 min-h-0 overflow-y-auto"
        >
          {!ticket.attachments || ticket.attachments.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-xs text-fg-muted mb-2">{t('No attachments yet')}</p>
              <p className="text-xs text-fg-muted">{t('Upload files to get started')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ticket.attachments.map((attachment: any) => {
                const isImage = isImageFile(attachment.filename, attachment.content_type);
                const fileIcon = getFileIcon(attachment.filename, attachment.content_type);
                const cachedImageUrl = imageCache[attachment.attachment_id];

                if (isImage) {
                  return (
                    <div key={attachment.attachment_id} className="relative group cursor-pointer rounded-lg overflow-hidden">
                      {cachedImageUrl ? (
                        <>
                          <img
                            src={cachedImageUrl}
                            alt={attachment.filename}
                            className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                            onClick={() => handleImageClick(attachment)}
                            title={t('Click to view full size')}
                          />
                          {/* A scrim is allowed here because it sits over an
                              image - the one place the quiet-glass standard
                              still permits a black overlay. */}
                          <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-all duration-300 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                              <Button
                                variant="neutral"
                                size="small"
                                ariaLabel={t('View')}
                                tooltip={t('View')}
                                className="rounded-full !p-2"
                                icon={<PiArrowsOutSimpleLight size={18} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageClick(attachment);
                                }}
                              />
                              <Button
                                variant="neutral"
                                size="small"
                                ariaLabel={t('Download')}
                                tooltip={t('Download')}
                                className="rounded-full !p-2"
                                icon={<PiCloudArrowDownLight size={18} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(attachment);
                                }}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <Skeleton variant="card" rows={2} className="h-40" />
                      )}
                    </div>
                  );
                } else {
                  return (
                    <Well
                      key={attachment.attachment_id}
                      className="flex items-center justify-between transition-colors hover:bg-surface-sunken"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 text-fg-body">{fileIcon}</div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-fg-strong truncate">{attachment.filename}</p>
                          <p className="text-xs text-fg-muted">{formatFileSize(attachment.size)}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="small"
                        ariaLabel={t('Download')}
                        tooltip={t('Download')}
                        className="ms-2 flex-shrink-0"
                        icon={<PiCloudArrowDownLight size={16} />}
                        onClick={() => handleDownload(attachment)}
                      />
                    </Well>
                  );
                }
              })}
            </div>
          )}
        </GlassCard>
      )}

      {/* COMMENTS — list scrolls, input pinned at the bottom */}
      {activeTab === 'comments' && (
        <GlassCard
          className="flex flex-col h-full overflow-hidden"
          bodyClassName="flex-1 min-h-0 flex flex-col"
        >
          {/* Comments list — the scrolling region */}
          <div className="flex-1 overflow-y-auto space-y-3 pe-2">
            {isLoadingComments ? (
              <Skeleton variant="list" rows={3} />
            ) : comments.length === 0 ? (
              <p className="text-xs text-fg-muted text-center py-4">{t('No comments yet')}</p>
            ) : (
              comments.map((comment: any, idx: number) => {
                // The API returns { author, author_type, text, timestamp }. This
                // used to read `content`/`created_at` and hard-code "You", so an
                // admin reply rendered as an empty "You" card. Read the real
                // fields, label by author_type, and set admin replies apart.
                const isAdmin = comment.author_type === 'admin' || comment.author === 'Admin';
                const when = comment.timestamp || comment.created_at;
                const body = comment.text ?? comment.content ?? '';
                return (
                  <Well
                    key={comment.id ?? idx}
                    className={isAdmin ? 'border-s-2 border-primary-400 bg-primary-50/40 dark:bg-primary-950/20' : undefined}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-fg-strong">
                        {isAdmin ? (comment.author || t('PONS support')) : t('You')}
                      </p>
                      <p className="text-xs text-fg-muted">
                        {when && format(toDate(when), 'MMM d, HH:mm')}
                      </p>
                    </div>
                    <RichText text={body} className="text-xs text-fg-body space-y-1 [&_strong]:font-medium [&_strong]:text-fg-strong" />
                  </Well>
                );
              })
            )}
          </div>

          {/* Comment input — pinned at the bottom */}
          <div className="mt-3 pt-3 border-t border-hairline-soft flex-shrink-0">
            <Textarea
              id="ticket-comment"
              label={t('Add a comment')}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={t('Add a comment...')}
              rows={2}
            />
            <div className="flex justify-end mt-2">
              <Button
                onClick={handlePostComment}
                disabled={!commentText.trim()}
                isLoading={isPostingComment}
                variant="primary"
                size="small"
                icon={<PiPaperPlaneTiltLight size={14} />}
              >
                {t('Post')}
              </Button>
            </div>
          </div>
        </GlassCard>
      )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/20 backdrop-blur-[2px] animate-fade-in p-4">
          <div className="relative max-w-5xl max-h-[90vh] glass rounded-lg overflow-hidden shadow-sm">
            <img src={selectedImage} alt={t('Full size')} className="max-w-full max-h-[85vh] object-contain" />
            <div className="absolute top-4 start-4 end-4 flex justify-between items-center">
              <Button
                variant="neutral"
                size="small"
                ariaLabel={t('Close')}
                tooltip={t('Close')}
                className="rounded-full !p-2"
                icon={<PiXLight size={20} />}
                onClick={handleCloseModal}
              />
              <Button
                variant="neutral"
                size="small"
                ariaLabel={t('Download')}
                tooltip={t('Download')}
                className="rounded-full !p-2"
                icon={<PiCloudArrowDownLight size={20} />}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = selectedImage;
                  link.download = 'attachment';
                  document.body.appendChild(link);
                  link.click();
                  link.parentNode?.removeChild(link);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetailsContent;
